import type { FleetCommandRow } from "@/api/client";
import type { IconName } from "@/components/icons";
import { CAP } from "@/auth/session";

/** THE COMMAND CATALOGUE — one entry per command, and both Fleet pages render
 *  themselves from it.
 *
 *  WHY THIS FILE EXISTS. Dattatray: *"also we can add more commands in future
 *  so make it cleaner!"* Adding a command should be one entry here, not a new
 *  screen and not a second copy of the state vocabulary. The alternative is
 *  the bug class §9k names — one fact with two homes — and this subsystem is
 *  where it would hurt most: a reason code spelled one way on the fleet log
 *  and another on Asset Control is indistinguishable from agreement until an
 *  operator reads the wrong one.
 *
 *  FOUR FIELDS DECIDE WHERE A COMMAND APPEARS, and none of them is cosmetic:
 *
 *    scope        whether it can run in BULK at all. You cannot meaningfully
 *                 fire-and-forget at 200 assets and have the result mean
 *                 anything, so one-shot commands are single-only and the
 *                 fleet page simply never offers them.
 *    needsStepUp  drives the confirmation panel. Immobilise asks; MOBILISE
 *                 NEVER DOES, because a locked bike and an expired
 *                 confirmation window is exactly when the release has to work.
 *    durable      whether the command is a STATE that waits for the vehicle
 *                 (a hold) or an ACTION that must expire and never be
 *                 replayed. A seat unlock that waited for reachability would
 *                 unlock the bike days later, somewhere else.
 *    options      renders the dropdown, from the device's own action numbers.
 */

/** The device's action numbers (`ACT_IMMOB_*` in the Telematics App's
 *  `bleControlPayload.h`). Orbit speaks the device's vocabulary; `tests/fleet`
 *  reads that header and fails if these drift. */
export const ACT = { MOBILIZE: 1, FREEWHEEL: 2, WHEELLOCK: 3 } as const;

export const ACT_LABEL: Record<number, string> = {
  1: "Mobilised", 2: "Freewheel (throttle cut)", 3: "Wheel lock",
};

/** What a command ROW should read, from its action alone.
 *
 *  THE ACTION IS THE WHOLE ANSWER and the `type` column is not. Rows were
 *  labelled `type + " · " + ACT_LABEL[action]`, and `record_command` wrote
 *  `type="immobilize"` for every command including a release — so a cancelled
 *  mobilise rendered as **"Immobilise · Mobilised"**, which Dattatray rightly
 *  called nonsense: it is either a mobilise or an immobilise, never both.
 *
 *  Deriving from the action also repairs rows ALREADY WRITTEN with the wrong
 *  type, which a server-side fix alone could not. */
export function commandLabel(action: number): string {
  if (action === ACT.MOBILIZE) return "Mobilise";
  const how = ACT_LABEL[action];
  return how ? `Immobilise · ${how}` : `action ${action}`;
}

/** Orbit's actor → level map. LABELS ONLY: the server derives the level from
 *  the caller's role and refuses one sent in a body, which is what makes the
 *  AND gate mean what it says.
 *
 *  LEVEL 8 IS "PLATFORM", NOT A COMPANY NAME. It read "Aphelion (platform)",
 *  and Dattatray caught it: *"I am super user! Then it should be just
 *  platform right? Super user is not asseociated with Aphelion!"* — which is
 *  the sentence `tests/tenancy/test_no_company_is_special.py` was written
 *  around ("Aphelion is not a special company! Only super admin is
 *  special!"). Aphelion Energy is CMP-001, an ordinary company with a plan;
 *  the platform operator is a ROLE anyone running Orbit could hold. Naming a
 *  company in a level label conflates the two in the place a customer reads. */
export const LEVEL_LABEL: Record<number, string> = {
  2: "Fleet manager (2)", 3: "Company (3)", 5: "Financier (5)",
  8: "Platform (8)",
};

/** `RS_*` from the firmware header. MAPPED, NEVER INVENTED — `tests/fleet`
 *  checks Orbit's copy against that file, because Orbit listening for one
 *  spelling while the device emits another has cost this product real bugs. */
export const REASON: Record<number, string> = {
  0: "OK", 1: "Interlock", 2: "Auth failed", 3: "Unsupported / malformed",
  4: "Device busy (booting)", 5: "Superseded", 6: "Already immobilised",
  7: "Replay (stale seq)", 8: "Link not secure", 9: "Expired",
  10: "No rights", 11: "No grant", 12: "Clock untrusted",
};

export const STATE_COLOR: Record<string, string> = {
  pending: "var(--t-mute)", sent: "var(--info)", received: "var(--warn)",
  applied: "var(--ok)", nacked: "var(--bad)", superseded: "var(--t-dim)",
  expired_undelivered: "var(--bad)", expired_unapplied: "var(--bad)",
  cancelled: "var(--t-dim)",
};

/** What each state MEANS for the vehicle — the column an operator actually
 *  needs, and the one a bare state name does not give them.
 *  "expired_unapplied" tells nobody anything; "device had it and gave up"
 *  tells them what to do next. */
export const STATE_MEANS: Record<string, string> = {
  pending: "not sent yet",
  sent: "sent, no answer yet",
  received: "device has it, WAITING FOR THE VEHICLE TO HALT",
  applied: "in force on the vehicle",
  nacked: "refused by the device",
  superseded: "replaced by a later command at this level",
  expired_undelivered: "never reached the device",
  expired_unapplied: "device had it and gave up",
  // NOT "cancelled". The cell shows the chip and then this sentence, so an
  // entry equal to its own key printed the word twice — "cancelled /
  // cancelled" — which Dattatray read as a rendering fault, and fairly.
  // Every entry here has to ADD something the chip does not already say.
  cancelled: "called off before the device got it",
};

export function reasonText(code: number | null | undefined): string {
  if (code == null || code === 0) return "—";
  return REASON[code] ?? `rs ${code}`;
}

/** One command's declaration. `key` matches the server's `fleet_command.type`
 *  and the batch header's `type`, so a batch of a future command needs no
 *  column added anywhere. */
export interface CommandSpec {
  key: string;
  label: string;
  /** can it run across many assets, or only one at a time */
  scope: "single" | "both";
  /** does issuing it require a fresh second factor */
  needsStepUp: boolean;
  /** a STATE that waits for the vehicle (a hold), or a one-shot ACTION */
  durable: boolean;
  /** the capability that guards the WRITE; the read is GATE_FLEET alone */
  cap: number;
  /** DESTRUCTIVE or RESTORATIVE, and it drives every colour and icon.
   *
   *  THIS IS IN THE CATALOGUE BECAUSE IT WAS HARDCODED ONCE AND WAS WRONG.
   *  The bulk panel's action button read `btn red` + a "blocked" icon with
   *  `{spec.label}` interpolated, so choosing MOBILISE produced a red
   *  "Mobilise 3" with a ban symbol on it — the release control dressed as
   *  the destructive one. Dattatray caught it on the deployed console.
   *
   *  It matters more than tidiness: everywhere else in this subsystem the
   *  release path is deliberately the unobstructed one (`needsStepUp` is
   *  false for mobilise for ever, because a locked bike and an expired
   *  confirmation window is exactly when freeing it has to work). A red
   *  danger button at that moment makes an operator hesitate, which undoes
   *  the whole point. */
  tone: "danger" | "safe";
  /** icon name from `@/components/icons` — ban for a restraint, check for a
   *  release. Paired with `tone` here so the two can never disagree.
   *
   *  `IconName`, not `string`: tsc rejected the loose type and it was right to.
   *  A typo'd icon name renders NOTHING, which on a command button is a
   *  control with no symbol rather than a visible error. */
  icon: IconName;
  /** the action numbers this command can carry, for the dropdown */
  options: { action: number; label: string }[];
  /** false until the firmware side exists — rendered, disabled, and labelled */
  built: boolean;
  /** one line under the name in the picker */
  hint: string;
}

export const COMMANDS: CommandSpec[] = [
  {
    key: "immobilize", label: "Immobilise", scope: "both",
    needsStepUp: true, durable: true, cap: CAP.IMMOBILIZE, built: true,
    tone: "danger", icon: "lock",
    options: [
      { action: ACT.FREEWHEEL, label: "Freewheel (throttle cut)" },
      { action: ACT.WHEELLOCK, label: "Wheel lock" },
    ],
    hint: "single or bulk · needs confirmation",
  },
  {
    key: "mobilize", label: "Mobilise", scope: "both",
    // NEVER a step-up. See the header: the release has to work at the moment
    // the confirmation window has expired, which is precisely when somebody
    // is standing next to a locked vehicle.
    needsStepUp: false, durable: true, cap: CAP.IMMOBILIZE, built: true,
    // SAFE: this one frees a vehicle. Never red, and an OPEN LOCK rather
    // than a tick — a tick means "that worked", not "this releases it".
    tone: "safe", icon: "unlock",
    options: [],
    hint: "single or bulk · no confirmation",
  },
  {
    key: "ignition", label: "Ignition on / off", scope: "single",
    needsStepUp: false, durable: false, cap: CAP.IMMOBILIZE, built: false,
    tone: "danger", icon: "blocked",
    options: [],
    hint: "single only · not built yet",
  },
  {
    key: "seat_unlock", label: "Seat unlock", scope: "single",
    needsStepUp: false, durable: false, cap: CAP.IMMOBILIZE, built: false,
    tone: "safe", icon: "unlock",
    options: [],
    hint: "single only · not built yet",
  },
];

export const BULK_COMMANDS = COMMANDS.filter((c) => c.scope === "both");

/** The class for a command's own action button.
 *
 *  `prominent` is the single primary action on a panel (bulk run) — teal when
 *  safe, because it still needs to read as THE button. A non-prominent safe
 *  control sits beside a destructive sibling (the per-asset pair), where
 *  ghost against red is the right hierarchy: the restraint is the deliberate
 *  act, the release is the quiet one. Danger is red either way. */
export function actionClass(spec: CommandSpec, prominent = false): string {
  if (spec.tone === "danger") return "btn red";
  return prominent ? "btn teal" : "btn ghost";
}

/** The accent a command is highlighted with when selected. */
export function toneVar(spec: CommandSpec): string {
  return spec.tone === "danger" ? "var(--bad)" : "var(--ok)";
}

export function commandSpec(key: string): CommandSpec | undefined {
  return COMMANDS.find((c) => c.key === key);
}

/** The state chip, shared so the two pages cannot colour a state differently.
 *  Accepts a bare state string as well as a row, because the fleet-wide log
 *  and the per-asset log carry different row shapes. */
export function StateChip({ state }: { state: string }) {
  const col = STATE_COLOR[state] ?? "var(--t-mute)";
  return (
    <span className="chip" title={STATE_MEANS[state] ?? state}
      style={{ color: col, background: `color-mix(in srgb, ${col} 13%, transparent)` }}>
      {state}
    </span>
  );
}

export function CommandStateChip({ c }: { c: FleetCommandRow }) {
  return <StateChip state={c.state} />;
}
